import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  FileText,
  Calendar,
  DollarSign,
  Receipt,
  ChevronDown,
  Download,
  Plus,
  ExternalLink,
} from 'lucide-react';
import { apiGet } from '@/utils/api';
import { formatCurrency, getStatusColor, getStatusLabel } from '@/services/billingService';
import { downloadPlPdf } from '@/services/expenseService';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';

interface ChargeCategory {
  libelle: string;
  total: number;
  tva: number;
  items: Array<{
    id: string;
    compte: string;
    description: string;
    supplierName?: string;
    amountHt: number;
    tvaAmount: number;
    reference?: string;
  }>;
}

interface PLStatement {
  period: {
    year: number;
    month: number;
    label: string;
  };
  produits: {
    venteServicesRealise: number;
    venteServicesEnCours: number;
    venteServicesPotentiel: number;
    totalProduitsExploitation: number;
  };
  charges: {
    byCategory: Record<string, ChargeCategory>;
    totalChargesExploitation: number;
    totalTvaDeductible: number;
    expenseCount: number;
  };
  resultatExploitation: number;
  tva: {
    tvaCollecte: number;
    tvaDeductible: number;
    tvaAVerser: number;
  };
  invoiceSummary: {
    total: number;
    paid: number;
    paidAmount: number;
    sent: number;
    sentAmount: number;
    overdue: number;
    overdueAmount: number;
    draft: number;
    draftAmount: number;
  };
  trend: Array<{
    month: number;
    year: number;
    label: string;
    revenueHt: number;
  }>;
  clients: Array<{
    tenantName: string;
    planName: string;
    invoiceNumber: string;
    status: string;
    subtotal: number;
    tva: number;
    total: number;
    paidAt: string | null;
  }>;
}

const getPLStatement = async (month?: string): Promise<PLStatement> => {
  const query = month ? `?month=${month}` : '';
  return apiGet<PLStatement>(`/billing/pnl${query}`);
};

export const PLStatementPage: React.FC = () => {
  const { toasts, removeToast, error, success } = useToast();
  const toastRef = useRef({ success, error });
  toastRef.current = { success, error };
  const [data, setData] = useState<PLStatement | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Track if data has been fetched to prevent double fetch in Strict Mode
  const hasFetchedRef = useRef(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const result = await getPLStatement(selectedMonth);
      setData(result);
    } catch (err) {
      console.error('Failed to fetch P&L statement:', err);
      toastRef.current.error('Erreur', 'Impossible de charger le compte de résultat');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    // Prevent double fetch in React Strict Mode
    if (hasFetchedRef.current && !selectedMonth) return;
    hasFetchedRef.current = true;
    fetchData();
  }, [selectedMonth, fetchData]);

  // Generate month options (current month + last 11 months)
  const monthOptions = React.useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthNames = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ];
      const label = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
      options.push({ value, label });
    }
    return options;
  }, []);

  // Calculate max revenue for chart scaling
  const maxRevenue = data?.trend ? Math.max(...data.trend.map(t => t.revenueHt), 1) : 1;

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Compte de Produits et Charges</h1>
            <p className="text-sm text-gray-500 mt-1">
              CPC - État financier mensuel (Format marocain)
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            </div>
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              Actualiser
            </button>
            <Link
              to="/admin/billing/expenses"
              className="flex items-center gap-2 px-4 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
            >
              <Plus size={16} />
              Gérer les charges
            </Link>
            <button
              onClick={async () => {
                try {
                  await downloadPlPdf(selectedMonth);
                  toastRef.current.success('PDF téléchargé', 'Le CPC a été téléchargé avec succès');
                } catch (err) {
                  toastRef.current.error('Erreur', 'Impossible de télécharger le PDF');
                }
              }}
              className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              <Download size={16} />
              Exporter PDF
            </button>
          </div>
        </div>

        {data && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={20} />
                  <span className="text-sm font-medium opacity-90">Produits (HT)</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(data.produits.venteServicesRealise)}</p>
                <p className="text-xs opacity-75 mt-1">Chiffre d'affaires réalisé</p>
              </div>

              <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-xl p-5 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown size={20} />
                  <span className="text-sm font-medium opacity-90">Charges</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(data.charges.totalChargesExploitation)}</p>
                <p className="text-xs opacity-75 mt-1">Total charges d'exploitation</p>
              </div>

              <div className={`bg-gradient-to-br ${data.resultatExploitation >= 0 ? 'from-blue-500 to-indigo-600' : 'from-orange-500 to-red-600'} rounded-xl p-5 text-white`}>
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign size={20} />
                  <span className="text-sm font-medium opacity-90">Résultat</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(data.resultatExploitation)}</p>
                <p className="text-xs opacity-75 mt-1">Résultat d'exploitation</p>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl p-5 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Receipt size={20} />
                  <span className="text-sm font-medium opacity-90">TVA à verser</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(data.tva.tvaAVerser)}</p>
                <p className="text-xs opacity-75 mt-1">TVA collectée - TVA déductible</p>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-3 gap-6">
              {/* Left Column: CPC Statement */}
              <div className="col-span-2 space-y-6">
                {/* Produits d'Exploitation */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
                    <h2 className="text-lg font-semibold text-white">I - PRODUITS D'EXPLOITATION</h2>
                  </div>
                  <div className="p-6">
                    <table className="w-full">
                      <tbody className="divide-y divide-gray-100">
                        <tr>
                          <td className="py-3 text-gray-600">Ventes de services (réalisé)</td>
                          <td className="py-3 text-right font-medium">{formatCurrency(data.produits.venteServicesRealise)}</td>
                        </tr>
                        <tr>
                          <td className="py-3 text-gray-600">Ventes de services (en cours)</td>
                          <td className="py-3 text-right font-medium text-blue-600">{formatCurrency(data.produits.venteServicesEnCours)}</td>
                        </tr>
                        <tr className="bg-green-50">
                          <td className="py-3 font-semibold text-green-800">TOTAL PRODUITS D'EXPLOITATION</td>
                          <td className="py-3 text-right font-bold text-green-800">{formatCurrency(data.produits.totalProduitsExploitation)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Charges d'Exploitation */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-red-600 to-rose-600 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-white">II - CHARGES D'EXPLOITATION</h2>
                    <Link
                      to="/admin/billing/expenses"
                      className="flex items-center gap-1 text-xs text-white/80 hover:text-white"
                    >
                      <Plus size={14} />
                      Ajouter une charge
                    </Link>
                  </div>
                  <div className="p-6">
                    {data.charges.byCategory && Object.keys(data.charges.byCategory).length > 0 ? (
                      <table className="w-full">
                        <thead>
                          <tr className="text-xs text-gray-500 uppercase border-b border-gray-200">
                            <th className="py-2 text-left">Compte</th>
                            <th className="py-2 text-left">Libellé</th>
                            <th className="py-2 text-right">Montant HT</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {Object.entries(data.charges.byCategory)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([compte, cat]) => (
                              <tr key={compte}>
                                <td className="py-3 font-mono text-sm text-gray-500">{compte}</td>
                                <td className="py-3 text-gray-600">{cat.libelle}</td>
                                <td className="py-3 text-right font-medium">{formatCurrency(cat.total)}</td>
                              </tr>
                            ))}
                          <tr className="bg-red-50">
                            <td colSpan={2} className="py-3 font-semibold text-red-800">
                              TOTAL CHARGES D'EXPLOITATION
                              <span className="text-xs font-normal text-red-600 ml-2">
                                ({data.charges.expenseCount} charges validées)
                              </span>
                            </td>
                            <td className="py-3 text-right font-bold text-red-800">
                              {formatCurrency(data.charges.totalChargesExploitation)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-center py-8">
                        <Receipt className="mx-auto text-gray-300 mb-3" size={40} />
                        <p className="text-gray-500 mb-4">
                          Aucune charge enregistrée pour cette période
                        </p>
                        <Link
                          to="/admin/billing/expenses"
                          className="inline-flex items-center gap-2 px-4 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                        >
                          <Plus size={16} />
                          Ajouter des charges
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                {/* Résultat */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className={`bg-gradient-to-r ${data.resultatExploitation >= 0 ? 'from-blue-600 to-indigo-600' : 'from-orange-600 to-red-600'} px-6 py-4`}>
                    <h2 className="text-lg font-semibold text-white">III - RÉSULTAT D'EXPLOITATION</h2>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Produits - Charges</span>
                      <span className={`text-2xl font-bold ${data.resultatExploitation >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {formatCurrency(data.resultatExploitation)}
                      </span>
                    </div>
                    <div className="mt-4 flex gap-4 text-sm">
                      <div className="flex-1 bg-gray-50 p-3 rounded-lg">
                        <p className="text-gray-500">Marge brute</p>
                        <p className="font-semibold">
                          {data.produits.venteServicesRealise > 0
                            ? ((data.resultatExploitation / data.produits.venteServicesRealise) * 100).toFixed(1)
                            : 0}%
                        </p>
                      </div>
                      <div className="flex-1 bg-gray-50 p-3 rounded-lg">
                        <p className="text-gray-500">Ratio charges/produits</p>
                        <p className="font-semibold">
                          {data.produits.venteServicesRealise > 0
                            ? ((data.charges.totalChargesExploitation / data.produits.venteServicesRealise) * 100).toFixed(1)
                            : 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* TVA Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-600 to-violet-600 px-6 py-4">
                    <h2 className="text-lg font-semibold text-white">SECTION TVA</h2>
                  </div>
                  <div className="p-6">
                    <table className="w-full">
                      <tbody className="divide-y divide-gray-100">
                        <tr>
                          <td className="py-3 text-gray-600">TVA collectée (20%)</td>
                          <td className="py-3 text-right font-medium">{formatCurrency(data.tva.tvaCollecte)}</td>
                        </tr>
                        <tr>
                          <td className="py-3 text-gray-600">TVA déductible (estimée)</td>
                          <td className="py-3 text-right font-medium text-red-600">-{formatCurrency(data.tva.tvaDeductible)}</td>
                        </tr>
                        <tr className="bg-purple-50">
                          <td className="py-3 font-semibold text-purple-800">TVA À VERSER À L'ÉTAT</td>
                          <td className="py-3 text-right font-bold text-purple-800">{formatCurrency(data.tva.tvaAVerser)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Right Column: Stats & Chart */}
              <div className="space-y-6">
                {/* Invoice Summary */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Résumé des factures</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-500">Payées</p>
                        <p className="text-lg font-semibold text-green-700">{data.invoiceSummary.paid}</p>
                      </div>
                      <p className="text-sm font-medium text-green-700">{formatCurrency(data.invoiceSummary.paidAmount)}</p>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-500">Envoyées</p>
                        <p className="text-lg font-semibold text-blue-700">{data.invoiceSummary.sent}</p>
                      </div>
                      <p className="text-sm font-medium text-blue-700">{formatCurrency(data.invoiceSummary.sentAmount)}</p>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-500">En retard</p>
                        <p className="text-lg font-semibold text-red-700">{data.invoiceSummary.overdue}</p>
                      </div>
                      <p className="text-sm font-medium text-red-700">{formatCurrency(data.invoiceSummary.overdueAmount)}</p>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-500">Brouillons</p>
                        <p className="text-lg font-semibold text-gray-700">{data.invoiceSummary.draft}</p>
                      </div>
                      <p className="text-sm font-medium text-gray-700">{formatCurrency(data.invoiceSummary.draftAmount)}</p>
                    </div>
                  </div>
                </div>

                {/* Trend Chart */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution du CA (HT)</h3>
                  <div className="space-y-3">
                    {data.trend.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-12">{item.label.substring(0, 3)}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div
                            className={`h-full ${idx === data.trend.length - 1 ? 'bg-indigo-600' : 'bg-indigo-300'} rounded-full transition-all`}
                            style={{ width: `${Math.max((item.revenueHt / maxRevenue) * 100, 2)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-700 w-24 text-right">
                          {formatCurrency(item.revenueHt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Client Breakdown Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
                <h2 className="text-lg font-semibold text-white">Détail par client</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Facture</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">HT</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">TVA</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">TTC</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.clients.map((client, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">{client.tenantName}</td>
                        <td className="px-6 py-4 text-gray-600">{client.planName}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="text-gray-400" />
                            <span className="text-sm">{client.invoiceNumber}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(client.status)}`}>
                            {getStatusLabel(client.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">{formatCurrency(client.subtotal)}</td>
                        <td className="px-6 py-4 text-right text-gray-500">{formatCurrency(client.tva)}</td>
                        <td className="px-6 py-4 text-right font-medium">{formatCurrency(client.total)}</td>
                      </tr>
                    ))}
                    {data.clients.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                          Aucune facture pour cette période
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default PLStatementPage;
